import React from "react";
import { AlertTriangle, CalendarClock, CalendarPlus, FileCheck2 } from "lucide-react";

/**
 * The four status cards above the tenancies table.
 *
 * Every number comes from `GET /api/admin/tenancies/summary`, so a card and
 * the rows beneath it can never state two different definitions of the same
 * German word — and each card reads the field that matches the FILTER of the
 * same name.
 *
 * That is why "Aktiv" reads `running` and not `display_status_counts.active`.
 * The "Aktiv" filter returns the contracts running today, which includes a
 * terminated one whose effective end is still ahead; the `active` bucket
 * excludes it, because its badge says "Gekündigt". Pairing the bucket with
 * that filter would print a smaller number above a longer list.
 *
 * "Kündigung offen" stays a bucket. The two therefore OVERLAP on purpose: one
 * terminated-but-running contract is counted by both, and the cards do not
 * add up to `total`. Anyone tempted to make them add up should read this twice
 * — the numbers are right, the arithmetic they suggest is not.
 *
 * The counts are org-wide and independent of which page of results is on
 * screen. Summing the loaded rows would silently mean "of the current page".
 */

const TONES = {
  active: {
    shell: "border-emerald-500/25 bg-emerald-500/[0.06]",
    icon: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
    value: "text-emerald-700 dark:text-emerald-300",
  },
  reserved: {
    shell: "border-sky-500/25 bg-sky-500/[0.06]",
    icon: "bg-sky-500/10 text-sky-600 dark:text-sky-300",
    value: "text-sky-700 dark:text-sky-300",
  },
  notice: {
    shell: "border-amber-500/25 bg-amber-500/[0.06]",
    icon: "bg-amber-500/10 text-amber-600 dark:text-amber-300",
    value: "text-amber-700 dark:text-amber-300",
  },
  neutral: {
    shell: "border-border bg-muted/40",
    icon: "bg-muted text-muted-foreground",
    value: "text-foreground",
  },
};

function StatCard({ icon: Icon, label, value, hint, tone = "neutral", testId, title }) {
  const t = TONES[tone] || TONES.neutral;
  return (
    <div
      className={`flex items-center gap-3 rounded-[10px] border px-4 py-[14px] ${t.shell}`}
      data-testid={testId}
      title={title || undefined}
    >
      <span
        aria-hidden="true"
        className={`flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[9px] ${t.icon}`}
      >
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
      </span>
      <div className="min-w-0">
        <p className="m-0 text-[11.5px] font-medium leading-tight text-muted-foreground">
          {label}
        </p>
        <p
          className={`m-0 mt-[3px] text-[26px] font-semibold leading-none tabular-nums ${t.value}`}
        >
          {value}
        </p>
        <p className="m-0 mt-[4px] truncate text-[11px] leading-tight text-muted-foreground">
          {hint}
        </p>
      </div>
    </div>
  );
}

/** Resolve a `{loading, error, value}` slot into what the card should show. */
function resolve(state, hint, pick) {
  if (state?.loading) return { value: "…", hint: "Wird geladen …" };
  if (state?.error) return { value: "—", hint: "Konnte nicht geladen werden." };
  const picked = pick ? pick(state?.value) : state?.value;
  if (picked == null) return { value: "—", hint };
  return { value: String(picked), hint };
}

/** The server's count of contracts running on the Swiss business day. */
function runningCount(summary) {
  const n = summary?.running;
  return typeof n === "number" ? n : null;
}

/** One derived-status count out of the summary payload. */
function countOf(summary, key) {
  const counts = summary?.display_status_counts;
  if (!counts || typeof counts !== "object") return null;
  const n = counts[key];
  return typeof n === "number" ? n : null;
}

export default function TenancyStatsCards({ statusSummary }) {
  const active = resolve(
    statusSummary,
    "Laufend, inkl. gekündigter",
    runningCount
  );
  const reserved = resolve(statusSummary, "Zukünftige Einzüge", (v) =>
    countOf(v, "reserved")
  );
  const notice = resolve(statusSummary, "Gekündigt, noch nicht ausgezogen", (v) =>
    countOf(v, "notice_given")
  );

  return (
    <div
      // Four across from `lg`, not `xl`: `lg` is exactly where the split
      // view appears, and 2x2 cards stacked above an already tight
      // master/detail row is the worst place to spend vertical space.
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
      data-testid="tenancy-stats"
    >
      <StatCard
        testId="kpi-tenancies-active"
        icon={FileCheck2}
        tone="active"
        label="Aktiv"
        value={active.value}
        hint={active.hint}
      />
      <StatCard
        testId="kpi-tenancies-reserved"
        icon={CalendarPlus}
        tone="reserved"
        label="Reserviert"
        value={reserved.value}
        hint={reserved.hint}
      />
      <StatCard
        testId="kpi-tenancies-notice"
        icon={AlertTriangle}
        tone="notice"
        label="Kündigung offen"
        value={notice.value}
        hint={notice.hint}
      />
      {/* "Endet bald" has NO org-wide source yet. The summary endpoint carries
          the five derived-status counts and `running`, none of which answers
          "ends within 30 days". Deriving it from the loaded rows would produce
          a PAGE-scoped number sitting beside three org-wide ones — the exact
          drift the summary service was written to end. It shows "—" until a
          server-side count exists, because a wrong number here is worse than
          no number. */}
      <StatCard
        testId="kpi-tenancies-ending-soon"
        icon={CalendarClock}
        tone="neutral"
        label="Endet bald"
        value="—"
        hint="Noch nicht verfügbar"
        title="Für „Endet bald“ gibt es noch keine organisationsweite Zählung. Eine Berechnung aus der aktuellen Seite wäre nur seitenbezogen und würde neben den anderen Karten eine falsche Zahl zeigen."
      />
    </div>
  );
}
